import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Receipt as ReceiptIcon, Users } from 'lucide-react';
import type { Order } from '@sebastian/shared';
import { api, ApiError } from '../lib/api';
import { useSettings, useTables } from '../hooks/queries';
import { useToast } from '../hooks/useToast';
import { useCurrency } from '../lib/currency';
import { formatClock, formatWaiting } from '../lib/format';
import { useNow } from '../hooks/useNow';
import { CloseOrderDialog, InvoiceBuyerSummary } from '../components/InvoiceBuyerDialog';
import { PaymentDialog } from '../components/PaymentDialog';
import { PrintReceiptButton, ReceiptSheet, SendToPrinterButton } from '../components/ReceiptSheet';
import { Badge, Button, Card, EmptyState, ErrorState, Modal, Spinner } from '../components/ui';

/**
 * The till view: every table currently carrying a bill, newest request first.
 * A cashier works entirely from here without touching the floor plan.
 */
export function PaymentsPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { money } = useCurrency();
  const now = useNow(30_000);

  const { data: tables, isLoading, error, refetch } = useTables();
  const [payingOrderId, setPayingOrderId] = useState<string | null>(null);
  const [receiptOrderId, setReceiptOrderId] = useState<string | null>(null);

  const billable = useMemo(
    () =>
      (tables ?? [])
        .filter((table) => table.activeOrder)
        .sort((a, b) => {
          // Bills that have been asked for come first.
          const rank = (status: string) => (status === 'WAITING_PAYMENT' ? 0 : status === 'PAID' ? 1 : 2);
          return (
            rank(a.activeOrder!.status) - rank(b.activeOrder!.status) ||
            new Date(a.activeOrder!.openedAt).getTime() - new Date(b.activeOrder!.openedAt).getTime()
          );
        }),
    [tables],
  );

  const { data: payingOrder } = useQuery({
    queryKey: ['order', payingOrderId ?? 'none'],
    queryFn: () => api.orders.get(payingOrderId!),
    enabled: Boolean(payingOrderId),
  });

  const { data: receipt } = useQuery({
    queryKey: ['receipt', receiptOrderId ?? 'none'],
    queryFn: () => api.orders.receipt(receiptOrderId!),
    enabled: Boolean(receiptOrderId),
  });
  // The full order, for the invoice-customer dialog next to the receipt.
  const { data: receiptOrder } = useQuery({
    queryKey: ['order', receiptOrderId ?? 'none'],
    queryFn: () => api.orders.get(receiptOrderId!),
    enabled: Boolean(receiptOrderId),
  });
  const { data: settings } = useSettings();
  // The order whose close is waiting on the walk-in / company question.
  const [closingOrderId, setClosingOrderId] = useState<string | null>(null);
  const { data: closingOrder } = useQuery({
    queryKey: ['order', closingOrderId ?? 'none'],
    queryFn: () => api.orders.get(closingOrderId!),
    enabled: Boolean(closingOrderId),
  });
  const beginClose = (orderId: string) =>
    settings?.invoicingEnabled ? setClosingOrderId(orderId) : closeOrder.mutate(orderId);

  const closeOrder = useMutation({
    mutationFn: (orderId: string) => api.orders.close(orderId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tables'] });
      setReceiptOrderId(null);
      setClosingOrderId(null);
      toast.success('Order closed. Table is free.');
    },
    onError: (caught: unknown) =>
      toast.error(caught instanceof ApiError ? caught.message : 'Could not close the order.'),
  });

  const requestPayment = useMutation({
    mutationFn: (orderId: string) => api.orders.requestPayment(orderId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['tables'] }),
    onError: (caught: unknown) =>
      toast.error(caught instanceof ApiError ? caught.message : 'Could not request payment.'),
  });

  return (
    <div className="mx-auto max-w-[1400px] px-5 py-6">
      <header className="mb-6">
        <h1 className="font-display text-3xl tracking-wide text-ink">Payments</h1>
        <p className="mt-1 text-sm text-ink-faint">
          Tables carrying a bill. Take payment, print the receipt, then close the table.
        </p>
      </header>

      {isLoading ? <Spinner label="Loading open bills…" /> : null}
      {error ? <ErrorState error={error} onRetry={() => void refetch()} /> : null}

      {!isLoading && billable.length === 0 ? (
        <EmptyState
          icon={<CreditCard size={32} />}
          title="No open bills"
          message="Every table is settled."
        />
      ) : null}

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {billable.map((table) => {
          const order = table.activeOrder!;
          const settled = order.status === 'PAID';
          return (
            <Card key={table.id} className="flex flex-col gap-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-display text-2xl leading-none text-ink">
                    Table {String(table.number).padStart(2, '0')}
                  </div>
                  <div className="mt-1.5 flex items-center gap-3 text-xs text-ink-faint">
                    <span className="inline-flex items-center gap-1">
                      <Users size={12} /> {order.guestCount}
                    </span>
                    <span>Opened {formatClock(order.openedAt)}</span>
                    <span className="tnum">{formatWaiting(order.openedAt, now)}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <Badge tone={settled ? 'positive' : order.status === 'WAITING_PAYMENT' ? 'caution' : 'neutral'}>
                    {settled ? 'Paid' : order.status === 'WAITING_PAYMENT' ? 'Bill requested' : 'In service'}
                  </Badge>
                  {order.unsentItemCount > 0 ? (
                    <Badge tone="danger">{order.unsentItemCount} unsent</Badge>
                  ) : null}
                </div>
              </div>

              <div className="flex items-end justify-between">
                <span className="text-xs text-ink-faint">Order #{order.orderNumber}</span>
                <span className="tnum font-display text-3xl leading-none text-accent-strong">
                  {money(order.grandTotalCents)}
                </span>
              </div>

              <div className="flex gap-2">
                {settled ? (
                  <>
                    <Button
                      variant="secondary"
                      block
                      icon={<ReceiptIcon size={16} />}
                      onClick={() => setReceiptOrderId(order.id)}
                    >
                      Receipt
                    </Button>
                    <Button
                      variant="primary"
                      block
                      loading={closeOrder.isPending && closingOrderId === order.id}
                      onClick={() => beginClose(order.id)}
                    >
                      Close table
                    </Button>
                  </>
                ) : (
                  <>
                    {order.status !== 'WAITING_PAYMENT' ? (
                      <Button
                        variant="secondary"
                        block
                        loading={requestPayment.isPending}
                        onClick={() => requestPayment.mutate(order.id)}
                      >
                        Request bill
                      </Button>
                    ) : null}
                    <Button
                      variant="primary"
                      block
                      icon={<CreditCard size={16} />}
                      disabled={order.grandTotalCents <= 0 || order.unsentItemCount > 0}
                      title={
                        order.unsentItemCount > 0
                          ? 'Unsent items must be sent or removed on the order screen first'
                          : undefined
                      }
                      onClick={() => setPayingOrderId(order.id)}
                    >
                      Take payment
                    </Button>
                  </>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {payingOrder ? (
        <PaymentDialog
          order={payingOrder as Order}
          open={payingOrderId !== null}
          onClose={() => setPayingOrderId(null)}
          onSettled={(settled) => {
            setPayingOrderId(null);
            setReceiptOrderId(settled.id);
          }}
        />
      ) : null}

      <Modal
        open={receiptOrderId !== null}
        onClose={() => setReceiptOrderId(null)}
        title="Receipt"
        size="md"
        footer={
          <>
            <PrintReceiptButton />
            {receiptOrderId ? (
              <SendToPrinterButton orderId={receiptOrderId} kind="RECEIPT" variant="secondary" />
            ) : null}
            <Button
              variant="primary"
              loading={closeOrder.isPending}
              onClick={() =>
                receiptOrderId &&
                (receiptOrder?.isInvite ? closeOrder.mutate(receiptOrderId) : beginClose(receiptOrderId))
              }
            >
              Close table
            </Button>
          </>
        }
      >
        {receiptOrder && settings?.invoicingEnabled ? (
          <div className="mb-3">
            <InvoiceBuyerSummary order={receiptOrder} />
          </div>
        ) : null}
        {receipt ? <ReceiptSheet receipt={receipt} /> : <Spinner />}
      </Modal>

      {closingOrder && !closingOrder.isInvite ? (
        <CloseOrderDialog
          order={closingOrder}
          open={closingOrderId !== null}
          closing={closeOrder.isPending}
          onClose={() => setClosingOrderId(null)}
          onConfirm={() => closeOrder.mutate(closingOrder.id)}
        />
      ) : null}
    </div>
  );
}
